import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { usePrevious } from 'react-use';

import { useReportError } from '@/hooks/useReportError';
import {
  processDPSuggestionsDataV2,
  useFetchDPSuggestions,
} from '@/modules/aiDP/hooks/useFetchDPSuggestions';
import { MatchableKind } from '@/modules/aiSuggestions/aiSuggestions.types';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AiSuggestionKind } from '@/types/schema';
import { debug } from '@/utils/diagnostics';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { DispositiveProvisionSuggestionsContext } from './dispositiveProvisionSuggestions.context';

function isLegacyDPRecipientSuggestionKind(kind: MatchableKind): boolean {
  return (
    kind === AiSuggestionKind.DpRecipientGrantor_1DiesFirst ||
    kind === AiSuggestionKind.DpRecipientGrantor_2DiesFirst ||
    kind === AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst ||
    kind === AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst
  );
}

function getLegacyDPKindFirstDeathClientId({
  kind,
  primaryClients,
}: {
  kind: MatchableKind;
  primaryClients: Array<{ id: string }> | null | undefined;
}): string | null {
  if (
    kind === AiSuggestionKind.DpRecipientGrantor_1DiesFirst ||
    kind === AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst
  ) {
    return primaryClients?.[0]?.id ?? null;
  }

  if (
    kind === AiSuggestionKind.DpRecipientGrantor_2DiesFirst ||
    kind === AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst
  ) {
    return primaryClients?.[1]?.id ?? null;
  }

  return null;
}

export function getKGV2CandidatesForLegacyDPKind({
  kind,
  suggestionsByKind,
  primaryClients,
  targetKind,
}: {
  kind: MatchableKind;
  suggestionsByKind: DispositiveProvisionSuggestionsContext['suggestionsByKind'];
  primaryClients: Array<{ id: string }> | null | undefined;
  targetKind: PulidKind | null;
}) {
  const legacySuggestions = suggestionsByKind[kind] ?? [];
  if (!isLegacyDPRecipientSuggestionKind(kind)) {
    return legacySuggestions;
  }

  const expectedFirstDeathClientId = getLegacyDPKindFirstDeathClientId({
    kind,
    primaryClients,
  });
  const v2Suggestions = (
    suggestionsByKind[AiSuggestionKind.DpRecipientV2] ?? []
  ).filter((suggestion) => {
    if (targetKind === PulidKind.Entity && !suggestion.entity?.id) {
      return false;
    }

    if (
      targetKind === PulidKind.TestamentaryEntity &&
      !suggestion.testamentaryEntity?.id
    ) {
      return false;
    }

    if (
      expectedFirstDeathClientId &&
      suggestion.dpFirstDeathGrantorClientProfile?.id !==
        expectedFirstDeathClientId
    ) {
      return false;
    }

    return true;
  });

  return [...legacySuggestions, ...v2Suggestions];
}

function useContextValue(
  props: DispositiveProvisionSuggestionsProviderProps
): DispositiveProvisionSuggestionsContext {
  const { reportError } = useReportError();
  const { primaryClients } = useHouseholdDetailsContext();

  const { initPreviouslyAcknowledgedSuggestions, getAcknowledgedSuggestion } =
    useSuggestionAcknowledgementContext();

  const [targetId, setTargetId] = useState<string>('');
  const prevTargetId = usePrevious(targetId);

  const [suggestionsByKind, setSuggestionsByKind] = useState<
    DispositiveProvisionSuggestionsContext['suggestionsByKind']
  >({});

  const [suggestionDocumentIds, setSuggestionDocumentIds] = useState<string[]>(
    []
  );

  useEffect(() => {
    if (props.targetId && !targetId) {
      debug(
        '[DispositiveProvisionSuggestionsProvider][useEffect] setting INITIAL targetId from props',
        { targetId: props.targetId }
      );
      setTargetId(props.targetId);
    }
  }, [props.targetId, targetId]);

  const targetKind = useMemo(
    () => (targetId ? getPulidKind(targetId) : null),
    [targetId]
  );

  const { refetch, loading } = useFetchDPSuggestions({
    targetId,
    isEntity: targetKind === PulidKind.Entity,
    isTestamentaryEntity: targetKind === PulidKind.TestamentaryEntity,
    skip: !targetId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      debug('[DispositiveProvisionSuggestionsProvider] query error', {
        targetId,
        error,
      });
      reportError(
        `error fetching dispositive provision suggestions: targetId=${targetId}`,
        error
      );
    },
    onCompleted: (data) => {
      debug('[DispositiveProvisionSuggestionsProvider] query completed', {
        targetId,
        targetKind,
      });

      const processedData = processDPSuggestionsDataV2(data);

      debug(
        '[DispositiveProvisionSuggestionsProvider] processing suggestions',
        {
          count: processedData.suggestions.length,
          targetId,
        }
      );

      setSuggestionsByKind(processedData.suggestionsByKind);
      setSuggestionDocumentIds(processedData.suggestionDocumentIds);
      initPreviouslyAcknowledgedSuggestions(processedData.suggestions);
    },
  });

  const refetchSuggestions = useCallback(
    (targetId: string) => {
      debug(
        '[DispositiveProvisionSuggestionsProvider] refetchSuggestions called',
        { targetId }
      );

      if (!targetId) return;
      const kind = getPulidKind(targetId);

      if (kind !== PulidKind.Entity && kind !== PulidKind.TestamentaryEntity)
        return;
      void refetch({
        targetId,
        isEntity: kind === PulidKind.Entity,
        isTestamentaryEntity: kind === PulidKind.TestamentaryEntity,
      });
    },
    [refetch]
  );

  useEffect(() => {
    debug(
      '[DispositiveProvisionSuggestionsProvider][useEffect] targetId updated',
      {
        targetId,
        prevTargetId,
      }
    );

    if (!targetId || targetId === prevTargetId) return;

    // Refetch suggestions for the new targetId
    refetchSuggestions(targetId);
  }, [
    initPreviouslyAcknowledgedSuggestions,
    prevTargetId,
    refetchSuggestions,
    targetId,
  ]);

  const getMatchableSuggestion = useCallback(
    (kind: MatchableKind, matchedId: string) => {
      debug(
        '[DispositiveProvisionSuggestionsProvider] getMatchableSuggestion called',
        {
          kind,
          matchedId,
          targetId,
        }
      );

      const matched = getAcknowledgedSuggestion(matchedId);

      const byKind = suggestionsByKind[kind] ?? [];
      const candidateSuggestions = isLegacyDPRecipientSuggestionKind(kind)
        ? getKGV2CandidatesForLegacyDPKind({
            kind,
            suggestionsByKind,
            primaryClients,
            targetKind,
          })
        : byKind;

      return candidateSuggestions.find((b) =>
        matched.some((m) => b.id === m.suggestionID)
      );
    },
    [
      getAcknowledgedSuggestion,
      primaryClients,
      suggestionsByKind,
      targetId,
      targetKind,
    ]
  );

  return {
    loading,
    targetId,
    setTargetId,
    targetKind,
    suggestionsByKind,
    getMatchableSuggestion,
    refetchSuggestions,
    suggestionDocumentIds,
  };
}

interface DispositiveProvisionSuggestionsProviderProps {
  /** The entity or testamentary entity ID to show suggestions for */
  targetId: string;
}

export function DispositiveProvisionSuggestionsProvider({
  children,
  ...props
}: PropsWithChildren<DispositiveProvisionSuggestionsProviderProps>) {
  const contextValue = useContextValue({ ...props });

  return (
    <DispositiveProvisionSuggestionsContext.Provider value={contextValue}>
      {children}
    </DispositiveProvisionSuggestionsContext.Provider>
  );
}
